import { use, useCallback, useMemo } from 'react';
import { GlobalContext, type GlobalContextValue } from '@/global/globalContext';
import {
  GlobalAction,
  type ApplicantSession,
  type AppSettings,
  type AuthUser,
  type BreadcrumbItem,
  type GlobalState,
} from '@/global/globalState';
import type { RoleCode } from '@/routes/roles';

function useGlobalContext(): GlobalContextValue {
  const context = use(GlobalContext);
  if (!context) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
}

export interface GlobalStateApi extends GlobalContextValue {
  /** Shows or hides the app-wide loading overlay, with what it says while shown. */
  setBusy: (busy: boolean, label?: string) => void;
  setError: (error: unknown) => void;
  login: (session: { user: AuthUser; token?: string }) => void;
  logout: () => void;
  applicantSignIn: (session: ApplicantSession) => void;
  applicantSignOut: () => void;
  /** Switches to one of the person's roles, by its code. */
  setActiveRole: (code: RoleCode) => void;
  clearSelection: () => void;
  setAppSettings: (settings: Partial<AppSettings>) => void;
  setBreadcrumb: (items: BreadcrumbItem[]) => void;
  resetAll: () => void;
}

/** App-wide client state plus typed helpers. See `src/global/globalState.ts`. */
export function useGlobalState(): GlobalStateApi {
  const { state, dispatch } = useGlobalContext();

  // Helpers depend only on the stable `dispatch`.
  const actions = useMemo(
    () => ({
      setBusy: (busy: boolean, label?: string) =>
        dispatch(
          busy
            ? { type: GlobalAction.Busy, ...(label ? { label } : {}) }
            : { type: GlobalAction.Idle },
        ),
      setError: (error: unknown) => dispatch({ type: GlobalAction.Error, error }),
      login: (session: { user: AuthUser; token?: string }) =>
        dispatch({ type: GlobalAction.LoggedIn, ...session }),
      logout: () => dispatch({ type: GlobalAction.LoggedOut }),
      applicantSignIn: (session: ApplicantSession) =>
        dispatch({ type: GlobalAction.ApplicantSignedIn, session }),
      applicantSignOut: () => dispatch({ type: GlobalAction.ApplicantSignedOut }),
      setActiveRole: (code: RoleCode) => dispatch({ type: GlobalAction.SetActiveRole, role: code }),
      clearSelection: () => dispatch({ type: GlobalAction.ClearSelection }),
      setAppSettings: (appSettings: Partial<AppSettings>) =>
        dispatch({ type: GlobalAction.SetAppSettings, appSettings }),
      setBreadcrumb: (breadcrumb: BreadcrumbItem[]) =>
        dispatch({ type: GlobalAction.SetBreadcrumb, breadcrumb }),
      resetAll: () => dispatch({ type: GlobalAction.ResetAll }),
    }),
    [dispatch],
  );

  return { state, dispatch, ...actions };
}

/** Reads one slice of global state. */
export function useGlobalSelector<T>(select: (state: GlobalState) => T): T {
  return select(useGlobalContext().state);
}

/**
 * `[id, setId]` shaped exactly like `useState<number | ''>('')`, so portal
 * pages can swap their local picker state for the shared selection.
 */
type SelectionTuple = [number | '', (id: number | '') => void];

function useSelection(
  pick: (state: GlobalState) => number | null,
  toAction: (id: number | null) => Parameters<GlobalContextValue['dispatch']>[0],
): SelectionTuple {
  const { state, dispatch } = useGlobalContext();
  const set = useCallback(
    (id: number | '') => dispatch(toAction(id === '' ? null : id)),
    // `toAction` is a module-level constant for every caller below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );
  return [pick(state) ?? '', set];
}

const selectStudent = (s: GlobalState) => s.selectedStudentId;
const selectChild = (s: GlobalState) => s.selectedChildId;
const selectStaff = (s: GlobalState) => s.selectedStaffId;
const selectSchool = (s: GlobalState) => s.activeSchoolId;
const selectYear = (s: GlobalState) => s.activeAcademicYearId;
const studentAction = (studentId: number | null) =>
  ({ type: GlobalAction.SetSelectedStudent, studentId }) as const;
const childAction = (studentId: number | null) =>
  ({ type: GlobalAction.SetSelectedChild, studentId }) as const;
const staffAction = (staffId: number | null) =>
  ({ type: GlobalAction.SetSelectedStaff, staffId }) as const;
const schoolAction = (schoolId: number | null) =>
  ({ type: GlobalAction.SetActiveSchool, schoolId }) as const;
const yearAction = (yearId: number | null) =>
  ({ type: GlobalAction.SetActiveAcademicYear, yearId }) as const;

/** Student portal: the student being viewed, shared across pages. */
export const useSelectedStudent = (): SelectionTuple => useSelection(selectStudent, studentAction);

/** Parent portal: the child being viewed, shared across pages. */
export const useSelectedChild = (): SelectionTuple => useSelection(selectChild, childAction);

/** Faculty portal: the teacher being viewed, shared across pages. */
export const useSelectedStaff = (): SelectionTuple => useSelection(selectStaff, staffAction);

/** The school chosen for the session (`''` when none is chosen). See `useCurrentSchool`. */
export const useActiveSchool = (): SelectionTuple => useSelection(selectSchool, schoolAction);

/** The academic year chosen for the session (`''` when none is chosen). */
export const useActiveAcademicYear = (): SelectionTuple => useSelection(selectYear, yearAction);
